import Head from 'next/head'

import SignageIcon from '@/components/icons/Signage'
import SupportIcon from '@/components/icons/Support'
import VehicleIcon from '@/components/icons/VehicleOutfitting'
import VideoIcon from '@/components/icons/Video'
import Layout from '@/components/Layout'
import DataTreeSvg from '@/public/img/Data-Tree.svg'
import CablingImg from '@/public/img/retail/Cabling.jpg'
import ProductManagementImg from '@/public/img/retail/Product-Management.jpg'
import RetailDisplayImg from '@/public/img/retail/Retail-Display-Solutions.jpg'
import HeaderImg from '@/public/img/retail/Retail-Header-Image.jpg'
import VehicleOutfittingImg from '@/public/img/retail/Vehicle-Outfitting.jpg'
import SwooshSvg from '@/public/img/Swoosh.svg'


export default function Retail(): JSX.Element {
	return (
		<div className="overflow-x-hidden">
			<Head>
				<title key="page-title">Retail Solutions | Pace International</title>
			</Head>

			<div className="relative">
				<img className="absolute bottom-0 max-w-none -ml-4 -mr-24 -mb-16 sm:-mb-20 sm:-mr-32 md:-mr-36 md:-mb-24 lg:-mb-20 lg:-mr-44 xl:-mb-16 xl:-mr-52 2xl:-mr-64 2xl:-mb-24" src={SwooshSvg} alt="" />

				<section className="relative container mx-auto pt-6 px-4 sm:px-6 md:pt-9 xl:pt-12 xl:px-8">
					<h2 className="font-extrabold text-green-400 md:text-lg lg:text-xl xl:text-2xl">Retail Market</h2>

					<div className="mt-4 md:flex md:mt-6 xl:mt-9">
						<div className="md:w-1/2">
							<h3 className="font-extrabold text-3xl md:text-4xl lg:text-5xl xl:text-6xl">Enhancing retail experiences</h3>
							<p className="mt-3 lg:text-lg xl:text-xl">When it comes to making sales and a positive customer experience, Pace is ready to provide end-to-end solutions for stores and their field technicians.</p>
						</div>

						<div className="md:w-1/2 md:ml-12">
							<img className="mt-12 mx-auto rounded-lg shadow-lg md:mt-0 md:rounded-xl xl:rounded-2xl" src={HeaderImg} alt="Header" />
						</div>
					</div>
				</section>
			</div>


			<div className="container mx-auto px-4 mt-24 md:mt-28 sm:px-6 xl:px-8">
				<section className="mt-24 md:mt-28">
					<div className="text-center">
						<h3 className="text-xl font-extrabold sm:text-2xl lg:text-3xl xl:text-4xl">What we offer the retail sales industry</h3>
						<p className="text-sm mt-3 sm:text-base md:mx-auto md:max-w-2xl lg:text-lg lg:max-w-5xl xl:max-w-6xl xl:text-xl">An effective retail display must resonate with customers without requiring constant upkeep. Its function, aesthetics, and updates should all work together before and after the display goes live. With Pace’s extensive sourcing and distribution network, we offer the latest technology in digital signage, video displays, and more – all prepped and ready to deliver an engaging shopping experience right out of the box. Plus, you’ll have a team of product experts that know your equipment best, including vehicle outfitting solutions for serving customers on the go.</p>
					</div>

					<div className="mt-10 md:flex md:items-start xl:mt-16">
						<div className="flex items-start md:w-1/2">
							<div className="w-1/2 flex flex-col items-center justify-center">
								<SignageIcon className="h-12 w-12 md:h-14 md:w-14 xl:w-16 xl:h-16 2xl:w-20 2xl:h-20"/>
								<div className="text-center mt-4 xl:mt-6">
									<h4 className="text-sm font-extrabold uppercase sm:text-base lg:text-lg">DIGITAL SIGNAGE</h4>
									<p className="text-xs mt-0.5 sm:text-sm lg:text-base xl:mt-1">Don’t let precious foot traffic miss that next big product release with the help of our digital displays.</p>
								</div>
							</div>

							<div className="w-1/2 flex flex-col items-center justify-center ml-9 sm:ml-12 md:ml-9 2xl:ml-16">
								<VideoIcon className="h-12 w-12 md:h-14 md:w-14 xl:w-16 xl:h-16 2xl:w-20 2xl:h-20" />
								<div className="text-center mt-4 xl:mt-6">
									<h4 className="text-sm font-extrabold uppercase sm:text-base lg:text-lg">VIDEO DISPLAYS</h4>
									<p className="text-xs mt-0.5 sm:text-sm lg:text-base xl:mt-1">Enhance the retail experience with video placement and custom content distribution.</p>
								</div>
							</div>
						</div>

						<div className="flex items-start mt-9 md:mt-0 md:w-1/2 md:ml-9 2xl:ml-16">
							<div className="w-1/2 flex flex-col items-center justify-center">
								<VehicleIcon className="h-12 w-12 md:h-14 md:w-14 xl:w-16 xl:h-16 2xl:w-20 2xl:h-20"/>
								<div className="text-center mt-4 xl:mt-6">
									<h4 className="text-sm font-extrabold uppercase sm:text-base lg:text-lg">VEHICLE OUTFITTING</h4>
									<p className="text-xs mt-0.5 sm:text-sm lg:text-base xl:mt-1">Our vehicle outfitting gives you the tools and consumable products you need for a successful install.</p>
								</div>
							</div>

							<div className="w-1/2 flex flex-col items-center justify-center ml-9 sm:ml-12 md:ml-9 2xl:ml-16">
								<SupportIcon className="h-12 w-12 md:h-14 md:w-14 xl:w-16 xl:h-16 2xl:w-20 2xl:h-20"/>
								<div className="text-center mt-4 xl:mt-6">
									<h4 className="text-sm font-extrabold uppercase sm:text-base lg:text-lg">TECHNICAL SUPPORT</h4>
									<p className="text-xs mt-0.5 sm:text-sm lg:text-base xl:mt-1">If ready-to-go product kits don’t do the trick, our tech team is always here, standing by.</p>
								</div>
							</div>
						</div>
					</div>
				</section>


				<section className="mt-16 sm:mt-20 md:mt-24 md:flex md:items-center xl:mt-32">
					<div className="md:w-1/2">
						<h3 className="text-xl font-extrabold sm:text-2xl xl:text-3xl">Vehicle outfitting</h3>

						<p className="text-sm mt-4 sm:text-base xl:text-xl xl:mt-6">We’ll outfit your in-field support vehicles with the tools, equipment, and consumables they need to be as efficient and responsive as possible. With a simple ordering system designed with installers in mind, your team can easily replenish items, while our kitting and consolidation services will reduce your overall costs.</p>
					</div>

					<div className="mt-12 max-w-md mx-auto md:max-w-full md:mt-0 md:w-1/2 md:ml-16">
						<img className="mx-auto rounded-lg shadow-lg md:rounded-xl xl:rounded-2xl"src={VehicleOutfittingImg} alt="Vehicle Outfitting" />
					</div>
				</section>


				<section className="mt-16 sm:mt-20 md:mt-24 md:flex md:flex-row-reverse md:items-center xl:mt-32">
					<div className="md:w-1/2 md:ml-16">
						<h3 className="text-xl font-extrabold sm:text-2xl xl:text-3xl">Retail display solutions</h3>

						<p className="text-sm mt-4 sm:text-base xl:text-xl xl:mt-6">There’s nothing less exciting for a customer than finding a blank screen. Even worse, a video that isn’t calibrated for the content it’s meant to promote. With Pace, you can provide retail locations with video screens or an entire TV wall – each one set to the proper resolution, output, and complete with the latest firmware updates. So, what are you waiting for? Let us help you add state of the art digital displays to your next endcap.</p>

						<p className="text-sm mt-4 sm:text-base xl:text-xl xl:mt-6">And don’t worry, our engineering team is here to help you design the perfect system if you’re looking to elevate your in-store game.</p>
					</div>

					<div className="mt-12 max-w-md mx-auto md:max-w-full md:mt-0 md:w-1/2">
						<img className="mx-auto rounded-lg shadow-lg md:rounded-xl xl:rounded-2xl" src={RetailDisplayImg} alt="Retail Displays" />
					</div>
				</section>


				<section className="mt-16 sm:mt-20 md:mt-24 md:flex md:items-center xl:mt-32">
					<div className="md:w-1/2">
						<h3 className="text-xl font-extrabold sm:text-2xl xl:text-3xl">Complete product management</h3>

						<p className="text-sm mt-4 sm:text-base xl:text-xl xl:mt-6">Pace brings all the pieces together so you can find a complete solution that works for you. No more juggling multiple contracts or navigating multiple vendor relationships. We only work with recognized suppliers to offer everything you need for your next project... all in one Pace.</p>

						<p className="text-sm mt-4 sm:text-base xl:text-xl xl:mt-6">Need more room for your own inventory? We also manage client products from our 70,000-sq. ft. warehouse in Rochester, MN – complete with unique ID numbers and climate-controlled housing.</p>
					</div>

					<div className="mt-12 max-w-md mx-auto md:max-w-full md:mt-0 md:w-1/2 md:ml-16">
						<img className="mx-auto rounded-lg shadow-lg md:rounded-xl xl:rounded-2xl" src={ProductManagementImg} alt="Product Managment" />
					</div>
				</section>


				<section className="mt-16 sm:mt-20 md:mt-24 md:flex md:flex-row-reverse md:items-center xl:mt-32">
					<div className="md:w-1/2 md:ml-16">
						<h3 className="text-xl font-extrabold sm:text-2xl xl:text-3xl">Custom cabling, assembly, and light manufacturing</h3>

						<p className="text-sm mt-4 sm:text-base xl:text-xl xl:mt-6">Our fully functional, custom cabling will ensure all systems are communicating effectively. And if you need assembly and/or some light manufacturing to get the project as close to the finish line as possible, we take care of that, too.</p>

						<ul className="list-disc ml-4 mt-4 text-sm space-y-2 sm:text-base sm:space-y-3 xl:space-y-4 xl:text-xl xl:mt-6">
							<li>Custom lengths</li>
							<li>Voice, audio, data, and video</li>
							<li>HDMI, fiber optic, coaxial, and ethernet</li>
						</ul>
					</div>

					<div className="mt-12 max-w-md mx-auto md:max-w-full md:mt-0 md:w-1/2">
						<img className="mx-auto rounded-lg shadow-lg md:rounded-xl xl:rounded-2xl" src={CablingImg} alt="Cabling" />
					</div>
				</section>


				<section className="mt-16 sm:mt-20 md:mt-24 xl:mt-32 xl:flex">
					<div className="hidden xl:w-max xl:block xl:h-max">
						<img src={DataTreeSvg} alt="" />
					</div>

					<div className="relative pb-16 xl:pb-0 xl:ml-24 xl:flex-1">
						<div className="absolute bottom-0 left-0 opacity-10 -ml-4 hidden sm:block md:-ml-6 xl:hidden">
							<img className="md:w-3/4 lg:w-5/6" src={DataTreeSvg} alt="" />
						</div>

						<div className="relative">
							<h3 className="text-xl font-extrabold sm:text-2xl xl:max-w-md xl:text-3xl">Pace connects all solutions for your business</h3>

							<div className="mt-9 md:flex">
								<div className="space-y-8 md:w-1/2">
									<div>
										<h4 className="font-extrabold text-sm text-green-400 uppercase sm:text-base lg:text-lg">dish satellite tv</h4>
										<p className="text-xs mt-0.5 sm:text-sm lg:text-base">Proudly offering DISH’s satellite TV programming for added value</p>
									</div>

									<div>
										<h4 className="font-extrabold text-sm text-green-400 uppercase sm:text-base lg:text-lg">Hardware solutions</h4>
										<p className="text-xs mt-0.5 sm:text-sm lg:text-base">Routers, splitters, extenders, and connectors that support the newest audio/video technology.</p>
									</div>

									<div>
										<h4 className="font-extrabold text-sm text-green-400 uppercase sm:text-base lg:text-lg">Installation Tools & supplies</h4>
										<p className="text-xs mt-0.5 sm:text-sm lg:text-base">In addition to DISH equipment, we supply all necessary audio and video installation equipment.</p>
									</div>

									<div>
										<h4 className="font-extrabold text-sm text-green-400 uppercase sm:text-base lg:text-lg">Custom cabling</h4>
										<p className="text-xs mt-0.5 sm:text-sm lg:text-base">With assembly and light manufacturing available, we supply fully functional cabling.</p>
									</div>
								</div>

								<div className="space-y-8 mt-8 md:mt-0 md:w-1/2 md:ml-20">
									<div>
										<h4 className="font-extrabold text-sm text-green-400 uppercase sm:text-base lg:text-lg">Professional A/V Distribution</h4>
										<p className="text-xs mt-0.5 sm:text-sm lg:text-base">Cat 5e/6 or Coax, easily convert HD video signals to HDMI using Pace HDMI Matrix solutions.</p>
									</div>

									<div>
										<h4 className="font-extrabold text-sm text-green-400 uppercase sm:text-base lg:text-lg">Routers and lte products</h4>
										<p className="text-xs mt-0.5 sm:text-sm lg:text-base">Hundreds of vendors help us supply all vital components for your connectivity needs.</p>
									</div>

									<div>
										<h4 className="font-extrabold text-sm text-green-400 uppercase sm:text-base lg:text-lg">Cellular boosters</h4>
										<p className="text-xs mt-0.5 sm:text-sm lg:text-base">Reliable voice and 4G LTE data signal for all major North American cell carriers.</p>
									</div>
								</div>
							</div>
						</div>
					</div>
				</section>
			</div>
		</div>
	)
}

Retail.Layout = Layout
